import { useApolloClient } from '@apollo/client';
import React from 'react';
import { getIncompleteStateNew, TableSkeleton } from 'src/common';
import { Modifier } from 'src/components/Modifier';
import {
  computeCumulativeStartingValues,
  convertToModifiers,
  convertToModifiersGql,
} from 'src/components/ProjectedRisk/util';
import { isQueryLoading } from 'src/graphql';
import { ScenarioQuery, useScenarioModifierQuery } from 'src/graphql/_generated';
import { getPercentage } from 'src/utils/misc';
import useVersion from 'src/hooks/useVersion';

interface ScenarioControlExpandContentProps {
  scenario: NonNullable<ScenarioQuery['scenario']>;
  controlId: string;
}

export const ScenarioControlExpandContent: React.FC<ScenarioControlExpandContentProps> = ({ scenario, controlId }) => {
  const client = useApolloClient();
  const { version } = useVersion();

  const {
    data: scenarioModifierData,
    loading,
    networkStatus,
  } = useScenarioModifierQuery({
    variables: { id: scenario.id, versionId: version },
    fetchPolicy: 'no-cache',
  });

  const isLoading = isQueryLoading({ loading, data: scenarioModifierData, networkStatus });
  const scenarioData = scenarioModifierData?.scenario;

  if (isLoading || !scenarioData) return <TableSkeleton />;

  const riskManagementPlans = scenarioData.riskManagementPlans || [];
  const rmp = riskManagementPlans.find((r) => r.control?.id === controlId);

  if (!rmp || !rmp.control || getIncompleteStateNew(scenario) === 'TECH') return null;

  const recommendedModifiers = rmp.recommendedModifiers ? convertToModifiers(rmp.recommendedModifiers) : null;

  const baseFrequencyTimes = getPercentage(
    scenarioData.baseRisk?.frequencyTimes,
    scenarioData.baseRisk?.frequencyYears,
  );

  const rmpIndex = riskManagementPlans.findIndex((r) => r.control?.id === controlId);
  const cumulativeStarting = computeCumulativeStartingValues(riskManagementPlans, rmpIndex, {
    frequencyTimes: baseFrequencyTimes,
    lower: scenarioData.baseRisk?.lower || 0,
    upper: scenarioData.baseRisk?.upper || 0,
  });

  return (
    <Modifier
      projectedScenario={{
        id: scenario.id,
        upper: scenarioData.baseRisk?.upper || 0,
        lower: scenarioData.baseRisk?.lower || 0,
        frequency_years: scenarioData.baseRisk?.frequencyYears || 0,
        frequency_times: baseFrequencyTimes,
      }}
      startingValues={cumulativeStarting}
      disabled={!!version}
      control={{
        ...rmp.control,
        modifiers: convertToModifiersGql(rmp.modifiers || []),
      }}
      onSave={async () => {
        await client.refetchQueries({ include: ['scenario', 'scenarioModifier'] });
      }}
      withCopy
      recommendedModifiers={recommendedModifiers}
    />
  );
};
